import clsx from 'clsx';
import React, { type CSSProperties, type ReactNode, type Ref } from 'react';

/**
 * Renders its one child element in place of its own, with the Slot's props merged onto it — the
 * `asChild` mechanism of the layout primitives (ADR 0073), after Radix's `Slot`. Internal: not a
 * package entry, so it adds no API and no dependency.
 *
 * Merge rules, child winning wherever the two can't both apply:
 * - `className` — joined (Slot's first).
 * - `style` — merged, the child's declarations winning.
 * - `on*` handlers — both run, the child's first.
 * - `ref` — both receive the element.
 * - anything else — the child's value wins.
 *
 * Exactly one child element is required; anything else throws (`React.Children.only`).
 *
 * @example
 * <Slot className="box" data-padding="block"><a href="/x">Link</a></Slot>
 * // → <a href="/x" class="box" data-padding="block">Link</a>
 */
export interface SlotProps extends React.HTMLAttributes<HTMLElement> {
    children?: ReactNode;
}

type AnyProps = Record<string, unknown>;

const isHandler = (key: string) => /^on[A-Z]/.test(key);

/** Gives every ref the same element: a callback ref is called, an object ref is assigned. */
const composeRefs =
    <T,>(...refs: Array<Ref<T> | undefined>) =>
    (node: T | null) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
        });
    };

/** The child element's own ref: `element.ref` on React 18, `props.ref` from React 19. */
const childRef = (child: React.ReactElement): Ref<HTMLElement> | undefined =>
    ((child.props as AnyProps).ref as Ref<HTMLElement> | undefined) ??
    (child as unknown as { ref?: Ref<HTMLElement> }).ref ??
    undefined;

const Slot = React.forwardRef<HTMLElement, SlotProps>(({ children, ...slotProps }, ref) => {
    const child = React.Children.only(children);
    if (!React.isValidElement(child)) {
        throw new Error('asChild needs exactly one React element as its child.');
    }

    const childProps = child.props as AnyProps;
    // Memoised so React sees a stable callback ref and doesn't detach and reattach it every render.
    const ownRef = childRef(child);
    const composedRef = React.useMemo(() => composeRefs(ref, ownRef), [ref, ownRef]);
    const merged: AnyProps = { ...slotProps, ...childProps };

    Object.keys(slotProps).forEach((key) => {
        const slotValue = (slotProps as AnyProps)[key];
        const childValue = childProps[key];
        if (isHandler(key) && typeof slotValue === 'function' && typeof childValue === 'function') {
            merged[key] = (...args: unknown[]) => {
                childValue(...args);
                slotValue(...args);
            };
        }
    });

    merged.className = clsx(slotProps.className, childProps.className as string | undefined) || undefined;
    merged.style = { ...slotProps.style, ...(childProps.style as CSSProperties | undefined) };
    merged.ref = composedRef;

    return React.cloneElement(child, merged);
});

Slot.displayName = 'Slot';

export { Slot };
